import React, { useEffect, useState } from 'react';
import { Button, Field, Table, Modal, Message, Input, Toast } from 'react-frontier';
import { Dropdown, Icon } from 'semantic-ui-react';
import { InventoryWarehouse, Location, Request, Shipment, SetLoading } from '../SiteClasses';
import Autocomplete from './Autocomplete';
import classNames from 'classnames';
import { bindFormChange, formatFullAddress, formatPlacesTemp } from '../SiteUtil';
import API from '../API';
import Validator from '../Validator';
import SearchRequestModal from './SearchRequestModal';

interface GenerateShipmentModalProps{
	open: boolean,
	onClose: ()=>void,
	shipment: Shipment,
	onGenerated: (new_shipment_id: number)=>void,
}

enum DestinationType{
	WAREHOUSE = 1,
	LOCATION = 2,
}

enum LocationType{
	ADDRESS = 1,
	ESTABLISHMENT = 2,
}

interface ShipmentForm{
	warehouse_id?: number,
	destination?: Location,
	item_ids: number[],
	date_return?: number,
	description?: string,
}

var GenerateShipmentModal = (props: GenerateShipmentModalProps)=>{
	var [destinationType, setDestinationType] = useState<DestinationType>(DestinationType.LOCATION);
	var [locationType, setLocationType] = useState<LocationType>(LocationType.ADDRESS);
	var [locationQuery, setLocationQuery] = useState<string>('');
	var [warehouses, setWarehouses] = useState<InventoryWarehouse[]>(null);
	var [prompts, setPrompts] = useState<string[]>(null);
	var [loading, setLoading] = useState<boolean>(false);
	var [form, setForm] = useState<ShipmentForm>({ item_ids: [] });
	var [selectedRequest, setSelectedRequest] = useState<Request>(null);
	var [searchRequestModal, setSearchRequestModal] = useState<boolean>(false);

	var pendingItems = props.shipment.items ? props.shipment.items.filter(i => !i.deleted && !(i.shipped && !i.delivered)) : [];
	var onFormChange = bindFormChange(form, setForm);

	useEffect(()=>{
		if(!props.open) return;
		setDestinationType(props.shipment.warehouse_id ? DestinationType.WAREHOUSE : DestinationType.LOCATION);
		setLocationType(LocationType.ADDRESS);
		setLocationQuery('');
		setForm({
			warehouse_id: null,
			destination: null,
			item_ids: [],
			date_return: props.shipment.date_return || null,
			description: props.shipment.description || '',
		});
		setSelectedRequest(null);
		setSearchRequestModal(false);
		setPrompts(null);
		setLoading(false);
		if(!warehouses){
			API.getWarehouses().then(res=>{
				if(!res.error) setWarehouses(res.data);
			});
		}
	}, [props.open]);

	var onRequestSelected = (request: Request, setLoading: SetLoading)=>{
		setLoading(true);
		API.getRequest(request.request_id).then(res=>{
			if(res.error) return Toast.error(res.message);
			setSelectedRequest(res.data);
			setSearchRequestModal(false);
		}).catch(()=>{
			Toast.error('Hubo un error inesperado cargando la solicitud.');
		}).finally(()=>{
			setLoading(false);
		});
	};

	var selectAll = ()=>{
		setForm(f => ({ ...f, item_ids: f.item_ids.length === pendingItems.length ? [] : pendingItems.map(i => i.item_id) }));
	};

	var toggleItem = (item_id: number)=>{
		setForm(f => ({
			...f,
			item_ids: f.item_ids.includes(item_id)
				? f.item_ids.filter(id => id !== item_id)
				: [...f.item_ids, item_id]
		}));
	};

	var onPlaceSelected = (loc: any)=>{
		var formatted = formatPlacesTemp(loc);
		setLocationQuery(formatFullAddress(formatted));
		setForm(f => ({ ...f, destination: formatted }));
	};

	var generate = ()=>{
		var { valid, prompts } = Validator(form, {
			...(destinationType === DestinationType.WAREHOUSE ? {
				warehouse_id: [{ rule: 'number', label: 'Almacén de destino' }],
			} : {
				destination: [{ rule: (val: Location)=> !!val && !!(val.city || val.lattitude), prompt: 'Selecciona una dirección de destino válida.' }],
				date_return: [{ rule: 'number', prompt: 'La fecha de retorno es obligatoria.' }],
			}),
			item_ids: [{ rule: (val: number[])=> val && val.length > 0, prompt: 'Selecciona al menos un equipo para el nuevo envío.' }],
		});
		setPrompts(prompts);
		if(!valid) return;

		setLoading(true);
		API.createShipment({
			request_id: selectedRequest?.request_id,
			parent_shipment_id: props.shipment.shipment_id,
			warehouse_id: destinationType === DestinationType.WAREHOUSE ? form.warehouse_id : null,
			destination: destinationType === DestinationType.LOCATION ? form.destination : null,
			item_ids: form.item_ids,
			date_return: destinationType === DestinationType.WAREHOUSE ? undefined : form.date_return,
			description: form.description,
		}).then(res=>{
			if(res.error) return setPrompts([res.message]);
			Toast.success('Envío generado correctamente');
			props.onGenerated(res.data.shipment_id);
			props.onClose();
		}).catch(()=>{
			setPrompts(['Hubo un error al generar el envío. (GSM-1)']);
		}).finally(()=>{
			setLoading(false);
		});
	};

	return <>
		<Modal open={props.open} onClose={props.onClose} size='small'>
			<Modal.Header>Generar envío</Modal.Header>
			<Modal.Content>
				<Field label='Solicitud'>
					<Input
						value={selectedRequest ? `Solicitud #${selectedRequest.request_id}` : ''}
						readonly
						placeholder='Selecciona una solicitud...'
						button={<Button text='Seleccionar' onClick={()=> setSearchRequestModal(true)} />}
					/>
				</Field>
				<Field label='Tipo de destino'>
					<Dropdown
						fluid
						selection
						value={destinationType}
						options={[
							{ key: DestinationType.LOCATION, value: DestinationType.LOCATION, text: 'Dirección' },
							{ key: DestinationType.WAREHOUSE, value: DestinationType.WAREHOUSE, text: 'Almacén' },
						]}
						onChange={(e, { value })=>{
							setDestinationType(value as DestinationType);
							if(value === DestinationType.LOCATION){
								setForm(f => ({ ...f, warehouse_id: undefined }));
							}else{
								setForm(f => ({ ...f, destination: undefined }));
								setLocationQuery('');
							}
						}}
					/>
				</Field>
				{destinationType === DestinationType.LOCATION ? (
					<>
					<Field label='Tipo de dirección'>
						<Dropdown
							fluid
							selection
							value={locationType}
							onChange={(e, { value })=>{
								setLocationType(value as LocationType);
								setForm(f => ({ ...f, destination: undefined }));
								setLocationQuery('');
							}}
							options={[
								{ key: LocationType.ADDRESS, value: LocationType.ADDRESS, text: 'Dirección exacta' },
								{ key: LocationType.ESTABLISHMENT, value: LocationType.ESTABLISHMENT, text: 'Recinto / Lugar público' },
							]}
						/>
					</Field>
					<Field label='Destino'>
						<Autocomplete
							key={locationType}
							value={locationQuery}
							onChange={setLocationQuery}
							onPlaceSelected={onPlaceSelected}
							types={locationType === LocationType.ESTABLISHMENT ? ['establishment'] : ['address']}
							placeholder={locationType === LocationType.ESTABLISHMENT ? 'Buscar recinto o lugar público...' : 'Buscar dirección de destino...'}
						/>
					</Field>
					</>
				) : (
					<Field label='Almacén de destino'>
						<Dropdown
							fluid
							selection
							placeholder='Selecciona un almacén...'
							value={form.warehouse_id}
							options={warehouses ? warehouses.map(w => ({ key: w.warehouse_id, value: w.warehouse_id, text: w.warehouse_name })) : []}
							onChange={(e, { value })=> setForm(f => ({ ...f, warehouse_id: value as number }))}
						/>
					</Field>
				)}
				{destinationType !== DestinationType.WAREHOUSE && (
					<Input
						label='Fecha de retorno'
						value={form.date_return}
						calendar={{ date: form.date_return, mode: 'date', format: 'DD/MM/YY' }}
						onChange={onFormChange('date_return')}
						style={{ width: '100%', marginBottom: 10 }}
					/>
				)}
				<Input
					label='Comentarios'
					value={form.description}
					onChange={onFormChange('description')}
					textarea
				/>
				<div style={{ textAlign: 'center', marginTop: 10 }}>
					<Button text='Seleccionar todos' color='black' onClick={selectAll} />
				</div>
				<Table
					title='Equipos'
					selectable
					fitted
					style={{ marginTop: 10 }}
					headers={[
						<div style={{ textAlign: 'center' }}>
							<i className="checkmark icon" style={{ marginRight: 0 }}></i>
						</div>,
						'ID', 'Tipo', 'Nombre', 'Serie'
					]}
					collapsingIndexes={[0, 1, 4]}
					onClick={(v: any[])=>{
						var itemId = v[1];
						toggleItem(itemId);
					}}
					data={pendingItems.length === 0 ? [] : pendingItems.map(item=>{
						var selected = form.item_ids.includes(item.item_id);
						return [
							<i className={classNames('large icon circle', {
								check: selected,
								outline: !selected,
							})}></i>,
							item.item_id,
							item.type_name,
							item.item_name || <Icon name='minus' />,
							item.serial_number || <Icon name='minus' />,
						];
					})}
				/>
				<Message type='error' list={prompts} />
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cerrar' basic onClick={props.onClose} />
				<Button text='Generar envío' color='black' loading={loading} onClick={generate} />
			</Modal.Actions>
		</Modal>
		<SearchRequestModal
			open={searchRequestModal}
			onClose={()=> setSearchRequestModal(false)}
			onSelected={onRequestSelected}
		/>
	</>;
};

export default GenerateShipmentModal;
